import { RefObject, useEffect } from 'react'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), area[href]'

const MANAGED = 'data-rsv-slide-a11y'
const PREV_TAB = 'data-rsv-prev-tabindex'
const PREV_NONE = '__none__'

const disableDotsFocus = (container: HTMLElement): void => {
  const dotsList = container.querySelector<HTMLElement>('.slick-dots')
  if (dotsList) {
    dotsList.setAttribute('aria-hidden', 'true')
    dotsList
      .querySelectorAll<HTMLElement>('button')
      .forEach((btn) => btn.setAttribute('tabindex', '-1'))
  }
}

const updateTabIndices = (container: HTMLElement): void => {
  disableDotsFocus(container)
  const slides = container.querySelectorAll('.slick-slide')
  slides.forEach((slide) => {
    const isCloned = slide.classList.contains('slick-cloned')
    const isActive = !isCloned && slide.classList.contains('slick-active')
    const focusableElements =
      slide.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)

    if (isCloned) {
      slide.setAttribute('aria-hidden', 'true')
    }

    focusableElements.forEach((el) => {
      if (isActive) {
        if (el.getAttribute(MANAGED) !== '1') return
        const prev = el.getAttribute(PREV_TAB)
        if (prev === PREV_NONE) {
          el.removeAttribute('tabindex')
        } else if (prev !== null) {
          el.setAttribute('tabindex', prev)
        }
        el.removeAttribute(PREV_TAB)
        el.removeAttribute(MANAGED)
        return
      }
      if (el.getAttribute(MANAGED) === '1') {
        el.setAttribute('tabindex', '-1')
        return
      }
      const current = el.getAttribute('tabindex')
      el.setAttribute(PREV_TAB, current === null ? PREV_NONE : current)
      el.setAttribute(MANAGED, '1')
      el.setAttribute('tabindex', '-1')
    })
  })
}

const restoreManagedInTree = (container: HTMLElement): void => {
  container.querySelectorAll<HTMLElement>(`[${MANAGED}="1"]`).forEach((el) => {
    const prev = el.getAttribute(PREV_TAB)
    if (prev === PREV_NONE) {
      el.removeAttribute('tabindex')
    } else if (prev !== null) {
      el.setAttribute('tabindex', prev)
    }
    el.removeAttribute(PREV_TAB)
    el.removeAttribute(MANAGED)
  })
}

const mutationShouldUpdate = (mutations: MutationRecord[]): boolean => {
  for (const m of mutations) {
    if (m.type === 'attributes') {
      const attr = m.attributeName
      if (attr === 'class' || attr === 'aria-hidden') {
        const t = m.target
        if (t instanceof HTMLElement && t.classList.contains('slick-slide')) {
          return true
        }
      }
    }
    if (m.type === 'childList') {
      if (
        Array.from(m.addedNodes).some(
          (node) =>
            node instanceof HTMLElement &&
            node.classList.contains('slick-slide'),
        )
      ) {
        return true
      }
    }
  }
  return false
}

/** Tab order for focusables inside inactive slick slides (react-slick sets aria-hidden there). */
export default function useSlickA11y(
  containerRef: RefObject<HTMLElement>,
  /** Skip when the slider DOM is absent (e.g. loading) or not used (single slide). */
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return
    const container = containerRef.current
    if (!container) return

    let rafId: number | null = null

    const scheduleUpdate = (): void => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = null
        updateTabIndices(container)
      })
    }

    scheduleUpdate()

    const observer = new MutationObserver((mutations) => {
      if (!mutationShouldUpdate(mutations)) return
      scheduleUpdate()
    })
    observer.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'aria-hidden'],
    })

    return () => {
      observer.disconnect()
      if (rafId !== null) cancelAnimationFrame(rafId)
      restoreManagedInTree(container)
    }
  }, [containerRef, enabled])
}
